import { fleetUserHelpers } from 'src/apps/fleet/app/components/fleetUserHelpers';
import { cloudEnterpriseAdminApiClient } from '../clients/cloudEnterpriseAdminApiClient';
import type { EnterpriseOffboardingType } from './cloudEnterpriseAdminApi.types';

export const getRoles = () =>
  cloudEnterpriseAdminApiClient.GET('/admin/v1/roles').then((r) => {
    if (!r.data) return null;

    return r.data
      .map((role) => ({
        ...role,
        name: fleetUserHelpers.getRoleName(role),
        type: fleetUserHelpers.getRoleType(role.name),
        description: fleetUserHelpers.getRoleDescription(role),
      }))
      .sort(fleetUserHelpers.sortRoles);
  });

export const getRolesByUser = (vars: { memberId: string }) =>
  cloudEnterpriseAdminApiClient
    .GET('/admin/v1/members/{memberId}/roles', {
      params: {
        path: {
          memberId: vars.memberId,
        },
      },
    })
    .then((r) => r.data ?? null);

export const addRoleOnUser = (vars: { memberId: string; roleId: string }) =>
  cloudEnterpriseAdminApiClient
    .PUT('/admin/v1/members/{memberId}/roles/{roleId}', {
      params: {
        path: {
          memberId: vars.memberId,
          roleId: vars.roleId,
        },
      },
    })
    .then((r) => r.data ?? null);

export const deleteRoleOnUser = (vars: { memberId: string; roleId: string }) =>
  cloudEnterpriseAdminApiClient
    .DELETE('/admin/v1/members/{memberId}/roles/{roleId}', {
      params: {
        path: {
          memberId: vars.memberId,
          roleId: vars.roleId,
        },
      },
    })
    .then((r) => r.data ?? null);

export const transferOwnership = (vars: { memberId: string }) =>
  cloudEnterpriseAdminApiClient
    .PUT('/admin/v1/members/{memberId}/transfer-ownership', {
      params: {
        path: {
          memberId: vars.memberId,
        },
      },
    })
    .then((r) => r.data ?? null);

export const getOrgSettings = () => {
  return cloudEnterpriseAdminApiClient
    .GET('/admin/v1/settings')
    .then((res) => res.data ?? null);
};

export const updateOrgSettings = (vars: { analyticsOptout: boolean }) => {
  return cloudEnterpriseAdminApiClient
    .PUT('/admin/v1/settings', {
      body: { analyticsOptout: vars.analyticsOptout },
    })
    .then((res) => res.data ?? null);
};

export const updateOrgDisplayName = (vars: { displayName: string }) =>
  cloudEnterpriseAdminApiClient
    .PUT('/admin/v1/settings/display-name', {
      body: { displayName: vars.displayName },
    })
    .then((res) => res.data ?? null);

export const enableEnterpriseMigration = (vars: { domain: string }) =>
  cloudEnterpriseAdminApiClient
    .POST('/admin/v1/domains/migrate', {
      body: { domain: vars.domain },
    })
    .then((res) => res.data ?? null);

export const enableEnterpriseDomainForwarding = (vars: { domain: string }) =>
  cloudEnterpriseAdminApiClient
    .POST('/admin/v1/domains/forward', {
      body: { domain: vars.domain },
    })
    .then((res) => res.data ?? null);

export const deleteDomain = (vars: { domainId: string }) =>
  // TODO: SW-19556
  new Promise<void>((resolve, reject) => {
    const { domainId } = vars;
    setTimeout(() => {
      if (Math.random() < 0.5) {
        resolve();
      } else {
        reject(new Error(`Failed to delete domain ${domainId} (simulated).`));
      }
    }, 500);
  });

export const makePrimaryDomain = (vars: { domainId: string }) => {
  // TODO: SW-19556
  const { domainId } = vars;
  return new Promise<void>((resolve, reject) => {
    const delay = 500;
    setTimeout(() => {
      if (Math.random() < 0.5) {
        resolve();
      } else {
        reject(
          new Error(`Failed to make domain ${domainId} primary (simulated).`)
        );
      }
    }, delay);
  });
};

export const activateUser = (vars: { memberId: string }) =>
  cloudEnterpriseAdminApiClient.POST('/admin/v1/members/{memberId}/activate', {
    params: {
      path: {
        memberId: vars.memberId,
      },
    },
  });

export const deactivateUser = (vars: { memberId: string }) =>
  cloudEnterpriseAdminApiClient.POST(
    '/admin/v1/members/{memberId}/deactivate',
    {
      params: {
        path: {
          memberId: vars.memberId,
        },
      },
    }
  );

export const enableMemberMdm = (vars: { memberId: string }) =>
  cloudEnterpriseAdminApiClient.POST(
    '/admin/v1/members/{memberId}/enable-mdm',
    {
      params: {
        path: {
          memberId: vars.memberId,
        },
      },
    }
  );

export const disableMemberMdm = (vars: { memberId: string }) =>
  cloudEnterpriseAdminApiClient.POST(
    '/admin/v1/members/{memberId}/disable-mdm',
    {
      params: {
        path: {
          memberId: vars.memberId,
        },
      },
    }
  );

export const deleteUser = (vars: {
  memberId: string;
  directDeleteUser: boolean;
}) => {
  return cloudEnterpriseAdminApiClient.POST(
    vars.directDeleteUser
      ? '/admin/v1/members/{memberId}/delete'
      : '/admin/v1/members/{memberId}/delete-mark',
    {
      params: {
        path: {
          memberId: vars.memberId,
        },
      },
    }
  );
};

export const deleteDevice = (vars: { memberId: string; deviceId: string }) =>
  cloudEnterpriseAdminApiClient
    .DELETE('/admin/v1/members/{memberID}/devices/{deviceID}', {
      params: {
        path: {
          deviceID: vars.deviceId,
          memberID: vars.memberId,
        },
      },
    })
    .then((res) => res.data ?? null);

export const startOffboarding = () =>
  cloudEnterpriseAdminApiClient
    .POST('/admin/v1/offboarding/start')
    .then((res) => res.data ?? null);

export const completeOffboarding = (vars: {
  offboardingType: EnterpriseOffboardingType;
}) =>
  cloudEnterpriseAdminApiClient
    .POST('/admin/v1/offboarding/complete', {
      body: {
        offboardingType: vars.offboardingType,
      },
    })
    .then((res) => res.data ?? null);
